const external = cornerstoneTools.external;
const BaseAnnotationTool = cornerstoneTools.importInternal('base/BaseAnnotationTool');
// State
const textStyle = cornerstoneTools.textStyle;
// import textStyle from './../../stateManagement/textStyle.js';
const addToolState = cornerstoneTools.addToolState;
const removeToolState = cornerstoneTools.removeToolState;
const getToolState = cornerstoneTools.getToolState;
const toolStyle = cornerstoneTools.toolStyle;
const toolColors = cornerstoneTools.toolColors;
// Manipulators
const moveNewHandle = cornerstoneTools.importInternal('manipulators/moveNewHandle');
// Drawing
const getNewContext = cornerstoneTools.importInternal('drawing/getNewContext');
const draw = cornerstoneTools.importInternal('drawing/draw');
const drawLine = cornerstoneTools.importInternal('drawing/drawLine');
const setShadow = cornerstoneTools.importInternal('drawing/setShadow');

const drawHandles = cornerstoneTools.importInternal('drawing/drawHandles');
const drawLinkedTextBox = cornerstoneTools.importInternal('drawing/drawLinkedTextBox');
const lineSegDistance = cornerstoneTools.importInternal('util/lineSegDistance');
const roundToDecimal = cornerstoneTools.importInternal('util/roundToDecimal');
const EVENTS = cornerstoneTools.EVENTS
const triggerEvent = cornerstoneTools.importInternal('util/triggerEvent');
const throttle = cornerstoneTools.importInternal('util/throttle');
const getPixelSpacing = cornerstoneTools.importInternal('util/getPixelSpacing');
const getModule = cornerstoneTools.getModule;

/**
 * @public
 * @class ParallelTool
 * @memberof Tools.Annotation
 * @classdesc Tool for creating two parallel lines.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class ParallelTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: 'Parallel',
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
      },
    };

    super(props, defaultProps);
  }

  createNewMeasurement(eventData) {
    // Create the measurement data for this tool with the end handle activated
    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      complete: false,
      value: '',
      slope: 1,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    if (data.visible === false) {
      return false;
    }

    return (
      lineSegDistance(element, data.handles.start, data.handles.end, coords) <
      25);
  }

  updateCachedStats(image, element, data) {
    // Just for avoiding warnings
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    const {
      handleRadius,
      drawHandlesOnHover,
      hideHandlesIfMoving,
      renderDashed,
    } = this.configuration;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);

    const lineDash = getModule('globalConfiguration').configuration.lineDash;

    for (let i = 0; i < toolData.data.length; i += 2) {
      const data = toolData.data[i];
      const data2 = toolData.data[i + 1];

      if (data.visible === false) {
        continue;
      }

      draw(context, context => {
        setShadow(context, this.configuration);

        // Differentiate the color of activation tool
        const color = toolColors.getColorIfActive(data);

        const lineOptions = { color };

        if (renderDashed) {
          lineOptions.lineDash = lineDash;
        }

        data.slope = -((data.handles.end.y - data.handles.start.y) / (data.handles.end.x - data.handles.start.x));
        drawLine(
          context,
          eventData.element,
          data.handles.start,
          data.handles.end,
          lineOptions
        );

        if (data2 != null) {
          data2.handles.end.y = data.slope * (data2.handles.start.x - data2.handles.end.x) + data2.handles.start.y;
          drawLine(
            context,
            eventData.element,
            data2.handles.start,
            data2.handles.end,
            lineOptions
          );
        }

        // Draw the handles
        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          hideHandlesIfMoving,
        };

        if (this.configuration.drawHandles) {
          drawHandles(context, eventData, data.handles, handleOptions);
          if (data2 != null) {
            drawHandles(context, eventData, data2.handles, handleOptions);
          }
        }

      });
    }

  }

  addNewMeasurement(evt, interactionType) {
    evt.preventDefault();
    evt.stopPropagation();

    const eventData = evt.detail;

    // Search for incomplete measurements
    const element = evt.detail.element;

    const measurementData = this.createNewMeasurement(eventData);
    addToolState(element, this.name, measurementData);

    const doneMovingCallback = success => {
      // DoneMovingCallback for first measurement.
      if (!success) {
        removeToolState(element, this.name, measurementData);
        return;
      }
      const toolData = getToolState(element, this.name);
      if (toolData != null && measurementData != null) {
        const index = toolData.data.findIndex(d => d.uuid == measurementData.uuid)
        if (index % 2 != 0) {
          const eventType = EVENTS.MEASUREMENT_COMPLETED;
          const eventData = {
            toolName: this.name,
            toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
            element,
            measurementData,
          };
          triggerEvent(element, eventType, eventData);
        }
      }
    };

    // Associate this data with this imageId so we can render it and manipulate it
    external.cornerstone.updateImage(element);

    moveNewHandle(
      eventData,
      this.name,
      measurementData,
      measurementData.handles.end,
      this.options,
      interactionType,
      doneMovingCallback
    );
  }

  onMeasureModified(ev) {

  }

  activeCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  passiveCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  enabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  disabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }
}
